/**
 * GoBDDeleteConfirmDialog - Dialog für GoBD-konforme Löschungen
 * 
 * Zeigt eine Warnung, dass Einträge nicht endgültig gelöscht werden,
 * sondern für GoBD-Compliance als "gelöscht" markiert bleiben.
 * Fordert optional eine Begründung an.
 */
import { useState, useEffect } from 'react';
import { AlertTriangle, ShieldAlert, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const MIN_REASON_LENGTH = 5;

export const GoBDDeleteConfirmDialog = ({
  open,
  onOpenChange,
  title = "Eintrag löschen",
  itemDescription = "diesen Eintrag",
  requireReason = false,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const [reason, setReason] = useState('');

  // Reset reason when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const isReasonValid = !requireReason || reason.trim().length >= MIN_REASON_LENGTH;

  const handleConfirm = () => {
    if (isReasonValid) {
      onConfirm(requireReason ? reason.trim() : null);
    }
  };

  const handleCancel = () => {
    setReason('');
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Möchten Sie {itemDescription} wirklich löschen?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* GoBD Compliance Warning */}
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
                  GoBD-Hinweis
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300/80">
                  Aus Gründen der Nachweispflicht wird dieser Eintrag nicht endgültig gelöscht, 
                  sondern als &ldquo;gelöscht&rdquo; markiert. Er erscheint nicht mehr in Auswertungen, 
                  bleibt aber im Änderungsprotokoll nachvollziehbar.
                </p>
              </div>
            </div>
          </div>

          {/* Reason Input (optional) */}
          {requireReason && (
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">
                Löschgrund <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Bitte geben Sie den Grund für die Löschung an (mind. 5 Zeichen)..."
                className="bg-input border-border text-foreground min-h-[80px] resize-none"
                data-testid="delete-reason-input"
                autoFocus
              />
              <div className="flex justify-between mt-2">
                <p className={`text-xs ${reason.length >= MIN_REASON_LENGTH ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                  {reason.length} / {MIN_REASON_LENGTH} Zeichen (minimum)
                </p>
                {reason.length > 0 && reason.length < MIN_REASON_LENGTH && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Noch {MIN_REASON_LENGTH - reason.length} Zeichen erforderlich
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="text-muted-foreground"
            disabled={isLoading}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isReasonValid || isLoading}
            className="bg-red-500 hover:bg-red-600"
            data-testid="confirm-delete-btn"
          >
            {isLoading ? 'Wird gelöscht...' : 'Löschen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook für einfache Verwendung des GoBD Delete Dialogs
 */
export const useGoBDDelete = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState({
    title: "Eintrag löschen",
    itemDescription: "diesen Eintrag",
    requireReason: false,
    onConfirmAction: null,
  });

  /**
   * Öffnet den Lösch-Dialog
   * @param {Object} config - Konfiguration für den Dialog
   * @param {string} config.title - Dialog-Titel
   * @param {string} config.itemDescription - Beschreibung des zu löschenden Elements
   * @param {boolean} config.requireReason - Ob ein Grund erforderlich ist
   * @param {Function} config.onConfirm - Callback mit optionalem reason-Parameter
   */
  const requestDelete = (config) => {
    setDeleteConfig({
      title: config.title || "Eintrag löschen",
      itemDescription: config.itemDescription || "diesen Eintrag",
      requireReason: config.requireReason || false,
      onConfirmAction: config.onConfirm,
    });
    setDialogOpen(true);
  };

  const handleConfirm = (reason) => {
    if (deleteConfig.onConfirmAction) {
      deleteConfig.onConfirmAction(reason);
    }
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  return {
    dialogOpen,
    setDialogOpen,
    deleteConfig,
    requestDelete,
    handleConfirm,
    handleCancel,
  };
};

export default GoBDDeleteConfirmDialog;
